import React, { useEffect, useState, useRef } from 'react';
import styled from "@emotion/styled";
import { useApolloClient, useMutation } from '@apollo/client';
import { Checkbox, IconArrowLeft } from 'kompare-ui-library';
import { useNavigate, useParams } from 'react-router-dom';
import { updateCheckoutHealthOfferData, i18n, mediaQuery, setProgressBarPercentage, showMessage, useAppDispatch, useAppSelector, useForm, useCheckoutHealthOfferQuery, setOfferId, saveConsent, processQueryResult, setIsContractorSameAsInsuredPersonStore } from 'src/common';
import { Flex, Text, Card, InsuredPersonForm, ContractorForm, DeliveryForm, OfferSummaryCard, SuperFooterBar, Loader, CheckboxRequirement, Button, allInsuredPersonFormItems } from 'src/components';
import { AppRoutes } from '../_router/app.routes';

const FlexStyle = styled(Flex)({
  marginBottom: 16
})

const TextStyle = styled(Text)({
  marginBottom: 32
})

const TextStyle2 = styled(Text)({
  marginBottom: 16
})

const CardStyle = styled(Card)({
  padding: '24px 12px',

  [mediaQuery('mobile')]: {
    padding: '24px 20px'
  }
})

const CardStyle2 = styled(Card)({
  padding: '16px 12px',

  [mediaQuery('mobile')]: {
    padding: '16px 20px'
  }
})

const OfferPage = () => {
  const insuredPersonForm = useForm();
  const contractorForm = useForm();
  const deliveryForm = useForm();

  const client = useApolloClient();

  // Ref za terms agreement checkbox
  const termsCheckboxRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const params = useParams<{ offerId: string }>();
  const offerId = params.offerId as string;

  const oneTimeToggle = useAppSelector((store) => store.model.offer.oneTimeToggle);
  const [isContractorSameAsInsuredPerson, setIsContractorSameAsInsuredPerson] = useState(true);
  const [isTermsAgreementChecked, setIsTermsAgreementChecked] = useState(false);
  const [isAllianzChecked, setIsAllianzChecked] = useState(false);
  const [isCheckboxRequirementVisible, setIsCheckboxRequirementVisible] = useState(false);
  const [addressData, setAddressData] = useState<{ insuredPersonAddress?: any, contractorAddress?: any, validation?: boolean }>({ insuredPersonAddress: {}, contractorAddress: {}, validation: false });

  const storedFormValues = useAppSelector((store) => store.model.offer.formValues);
  const storedIsContractorSameAsInsuredPerson = useAppSelector((store) => store.model.offer.isContractorSameAsInsuredPerson);
  const flowType = useAppSelector((store) => store.state.flow.flowType);
  const soId = useAppSelector((store) => store.model.offer.soId);

  useEffect(() => {
    dispatch(setProgressBarPercentage(60));
  }, []);

  useEffect(() => {
    dispatch(setOfferId(offerId));
  }, [offerId]);

  const [updateCheckoutHealthOffer] = useMutation(updateCheckoutHealthOfferData);
  const [saveConsentMutation] = useMutation(saveConsent);
  // const [sendOfferAcceptedMutation] = useMutation(sendOfferAccepted);

  const getCheckoutHealthOfferQuery = useCheckoutHealthOfferQuery({
    variables: { offerId },
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      processQueryResult(result);
    }
  });

  const setIsSame = (value: boolean) => {
    setIsContractorSameAsInsuredPerson(value);
    dispatch(setIsContractorSameAsInsuredPersonStore(value));
  }

  useEffect(() => {
    if (getCheckoutHealthOfferQuery.data) {
      const { checkoutHealthOfferData: data } = getCheckoutHealthOfferQuery.data.health || {};
      setIsSame(storedIsContractorSameAsInsuredPerson !== undefined 
        ? storedIsContractorSameAsInsuredPerson 
        : (data?.contractor?.isSamePerson ?? true));
      if (data) {
        setAddressData({
          insuredPersonAddress: data.insuredPerson ? {
            name: `${data.insuredPerson.firstName?.value || ""} ${data.insuredPerson.lastName?.value || ""}`.trim() || storedFormValues?.insuredPerson?.fullName,
            street: data.insuredPerson.street?.value || storedFormValues?.insuredPerson?.street,
            houseNumber: data.insuredPerson.houseNumber?.value || storedFormValues?.insuredPerson?.houseNumber,
            city: data.insuredPerson.city?.value || storedFormValues?.insuredPerson?.city,
            zipCode: data.insuredPerson.zipCode?.value || storedFormValues?.insuredPerson?.zipCode,
            streetKompareId: data.insuredPerson.streetKompareId?.value || storedFormValues?.insuredPerson?.streetKompareId,
            cityKompareId: data.insuredPerson.cityKompareId?.value || storedFormValues?.insuredPerson?.cityKompareId
          } : undefined,
          contractorAddress: data.contractor ? {
            name: (data.contractor.person ? `${data.contractor.person.firstName?.value ?? ""} ${data.contractor.person.lastName?.value ?? ""}`.trim() : data.contractor.company?.companyName?.value) || (storedFormValues?.contractor?.fullName || storedFormValues?.contractor?.companyName),
            street: (data.contractor.person ? data.contractor.person.street?.value : data.contractor.company?.street?.value) || storedFormValues?.contractor?.street,
            houseNumber: (data.contractor.person ? data.contractor.person.houseNumber?.value : data.contractor.company?.houseNumber?.value) || storedFormValues?.contractor?.houseNumber,
            city: (data.contractor.person ? data.contractor.person.city?.value : data.contractor.company?.city?.value) || storedFormValues?.contractor?.city,
            zipCode: (data.contractor.person ? data.contractor.person.zipCode?.value : data.contractor.company?.zipCode?.value) || storedFormValues?.contractor?.zipCode,
            streetKompareId: (data.contractor.person ? data.contractor.person.streetKompareId?.value : data.contractor.company?.streetKompareId?.value) || storedFormValues?.contractor?.streetKompareId,
            cityKompareId: (data.contractor.person ? data.contractor.person.cityKompareId?.value : data.contractor.company?.cityKompareId?.value) || storedFormValues?.contractor?.cityKompareId,
          } : undefined,
          validation: false
        });
      }
    }

  }, [getCheckoutHealthOfferQuery.data]);

  if (getCheckoutHealthOfferQuery.loading) return <Loader page />;
  if (getCheckoutHealthOfferQuery.error) navigate(AppRoutes.CUSTOMER.fullPath);

  // Destrukturiranje podataka
  const { health } = getCheckoutHealthOfferQuery.data || {};
  const { checkoutHealthOffer, checkoutHealthOfferDocuments, checkoutHealthOfferData } = health || {};

  const formData = checkoutHealthOfferData;

  const onSubmitClick = async () => {
    const isInsuredPersonFormValid = insuredPersonForm.validateForm();
    const isContractorFormValid = isContractorSameAsInsuredPerson ? true : contractorForm.validateForm();
    const isDeliveryFormValid = deliveryForm.validateForm();

    if (!isInsuredPersonFormValid) {
      insuredPersonForm.scrollIntoView();
      return;
    }

    if (!isContractorFormValid && !isContractorSameAsInsuredPerson) {
      contractorForm.scrollIntoView();
      return;
    }

    // Mapiranje podataka na izlazu iz forme - delivery -- ubačeno je ovdje jer je validacija potrebna samo u slučaju asEntity = 'third'
    const delivery = deliveryForm.getValues();
    delivery.streetsFound = undefined;

    if (!isDeliveryFormValid && delivery.asEntity === 'third') {
      deliveryForm.scrollIntoView();
      return;
    }

    if (!isTermsAgreementChecked) {
      termsCheckboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setIsCheckboxRequirementVisible(true);
      return;
    }

    // Mapiranje podataka na izlazu iz forme - insuredPerson
    const insuredPerson = insuredPersonForm.getValues();
    insuredPerson.fullName = undefined;
    insuredPerson.streetsFound = undefined;
    delete (insuredPerson as any).birthDate;
    delete (insuredPerson as any).phoneNumber;
    delete (insuredPerson as any).gender;

    // Mapiranje podataka na izlazu iz forme - contractor
    let contractorData: any = {};
    let contractor: any = {};
    if (isContractorSameAsInsuredPerson) {
      contractor = {
        isSamePerson: true,
        personType: 'person'
      }
    } else {
      contractorData = contractorForm.getValues();
      if (contractorData.personType === 'person') {
        contractor = {
          isSamePerson: false,
          personType: 'person',
          person: {
            firstName: contractorData.firstName,
            lastName: contractorData.lastName,
            birthDate: contractorData.birthDate,
            oib: contractorData.oib,
            partnerContactEmail: contractorData.partnerContactEmail,
            city: contractorData.city,
            cityKompareId: contractorData.cityKompareId,
            zipCode: contractorData.zipCode,
            street: contractorData.street,
            streetKompareId: contractorData.streetKompareId,
            houseNumber: contractorData.houseNumber
          }
        }
      } else {
        contractor = {
          isSamePerson: false,
          personType: 'company',
          company: {
            companyName: contractorData.companyName,
            oib: contractorData.oib,
            partnerContactEmail: contractorData.partnerContactEmail,
            city: contractorData.city,
            cityKompareId: contractorData.cityKompareId,
            zipCode: contractorData.zipCode,
            street: contractorData.street,
            streetKompareId: contractorData.streetKompareId,
            houseNumber: contractorData.houseNumber,
            streetAndHouseNumber: `${contractorData.street} ${contractorData.houseNumber}`
          }
        }
      }
    }

    const data = {
      offerId,
      insuredPerson,
      contractor,
      delivery
    }

    try {
      await updateCheckoutHealthOffer({
        variables: {
          params: data,
        },
        onCompleted: () => {
          client.refetchQueries({
            include: ['getCheckoutHealthOfferQuery'] // upit koji želite ponovo dohvatiti
          });
        }
      });
    } catch (e) {
      console.error(e);
      return;
    }

    saveConsentMutation({
      variables: {
        params: { offerId, consent: { healthValidDataConsent: isTermsAgreementChecked, allianzGDPR: checkoutHealthOffer?.supplementary?.brandCode === 'allianz' ? isAllianzChecked : undefined, generalTOSConsent: true, healthNeedConsent: true, healthEmailDocumentationConsent: true, fourteenDayCancellationPolicy: true } }
      }
    });

    // sendOfferAcceptedMutation({
    //   variables: { offerId }
    // });

    navigate(`${AppRoutes.CHECKOUT.fullPath}/${offerId}`);
    dispatch(showMessage({ message: i18n.t('message.acceptedTermsAndConditions'), type: 'success', duration: 6500 }));
  }

  const handleAddressChangeInsured = (address: any) => {
    setAddressData({ ...addressData, insuredPersonAddress: { ...addressData.insuredPersonAddress, ...address }, validation: true });
  };

  const handleAddressChangeContractor = (address: any) => {
    setAddressData({ ...addressData, contractorAddress: { ...addressData.contractorAddress, ...address }, validation: true });
  };

  const handleBackButton = () => {
    if (soId)
      navigate(`${AppRoutes.SO.fullPath}/${soId}`);
    else
      navigate(`${AppRoutes.OFFERS.fullPath}`);
  }

  if (!formData) return null;
  return (
    <>
      <Flex vertical gap={24}>
        <Flex vertical gap={8}>
          <Text type="xLarge" bold>{i18n.t('pages.offerPage.title')}</Text>
          <Text type="base">{i18n.t('pages.offerPage.description')}</Text>
        </Flex>
        <OfferSummaryCard offer={checkoutHealthOffer} documents={checkoutHealthOfferDocuments !== null ? checkoutHealthOfferDocuments as { title: string; src: string; }[] : undefined} oneTimeToggle={oneTimeToggle} />
        <CardStyle
          radius={12}
          title={
            <FlexStyle vertical gap={16}>
              <Text type="base" bold>{i18n.t('pages.offerPage.basicInformation')}</Text>
              <Text type="base" bold>{i18n.t('pages.offerPage.insuredPerson')}</Text>
            </FlexStyle>
          }>
          <Flex vertical gap={24}>
            <InsuredPersonForm insuredPerson={formData.insuredPerson} ref={insuredPersonForm.formRef} onAddressChange={handleAddressChangeInsured} storedValues={storedFormValues?.insuredPerson} formItems={flowType === 'agent' ? allInsuredPersonFormItems : allInsuredPersonFormItems.filter((item) => item !== 'insuranceNumber')} />
            <Checkbox checked={isContractorSameAsInsuredPerson} onChange={() => setIsSame(!isContractorSameAsInsuredPerson)} label={i18n.t('components.forms.insuredPersonForm.sameAsInsuredPerson')} />
          </Flex>
        </CardStyle>
        {!isContractorSameAsInsuredPerson &&
          <CardStyle
            radius={12}
            title={
              <TextStyle type="base" bold>{i18n.t('pages.offerPage.contractor')}</TextStyle>
            }>
            <ContractorForm contractor={formData.contractor} ref={contractorForm.formRef} onAddressChange={handleAddressChangeContractor} storedValues={storedFormValues?.contractor} />
          </CardStyle>}
        <CardStyle
          radius={12}
          title={
            <TextStyle2 type="base" bold>{i18n.t('pages.offerPage.delivery')}</TextStyle2>
          }>
          <DeliveryForm hideContractorOption={isContractorSameAsInsuredPerson} delivery={formData.delivery} addressData={addressData} ref={deliveryForm.formRef} storedValues={storedFormValues?.delivery} />
        </CardStyle>
        <CardStyle2
          radius={12}
          title={
            <TextStyle2 type="base" bold>{i18n.t('pages.offerPage.terms')}</TextStyle2>
          }>
          <Flex vertical gap={8}>
            <div ref={termsCheckboxRef}>
              <Checkbox checked={isTermsAgreementChecked} onChange={(checked: boolean) => setIsTermsAgreementChecked(checked)} checkboxSize='small' label={<Text type='tiny'>{i18n.t('pages.offerPage.termsDescription')}</Text>} />
            </div>
            {checkoutHealthOffer?.supplementary?.brandCode === "allianz" && <Checkbox checked={isAllianzChecked} checkboxSize='small' onChange={(checked: boolean) => setIsAllianzChecked(checked)} label={<Text type='tiny'>{i18n.t('pages.offerPage.allianzNotifications')}</Text>} />}
          </Flex>
        </CardStyle2>

        <CheckboxRequirement visible={isCheckboxRequirementVisible} text={i18n.t('pages.offerPage.checkboxRequirement')} />
      </Flex>
      <SuperFooterBar>
        <Button btnType="textBlack" iconPosition="left" text={i18n.t('pages.offerPage.backButton')} onClick={handleBackButton}>{<IconArrowLeft />}</Button>
        <Button btnType="primary" text={i18n.t('pages.offerPage.submit')}
          onClick={() => { onSubmitClick() }} />
      </SuperFooterBar>
    </>
  );
}

export default OfferPage;
